import React from 'react';
import { CheckCircle, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PricingFeature, getCategoryIcon, getCategoryColor } from '@/data/pricingPlans';

interface PricingFeatureListProps {
  features: string[] | PricingFeature[];
  variant?: 'simple' | 'detailed' | 'comparison';
  planId?: string;
  showCategories?: boolean;
  className?: string;
}

interface FeatureIconProps {
  included: boolean | string;
  variant?: 'simple' | 'detailed' | 'comparison';
}

const FeatureIcon: React.FC<FeatureIconProps> = ({ included, variant = 'simple' }) => {
  if (typeof included === 'string') {
    return (
      <span className={`text-sm font-medium ${
        variant === 'comparison' ? 'text-gray-700' : 'text-primary'
      }`}>
        {included}
      </span>
    );
  }
  
  return included ? (
    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
  ) : (
    <X className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
  );
};

export const PricingFeatureList: React.FC<PricingFeatureListProps> = ({
  features,
  variant = 'simple',
  planId,
  showCategories = false,
  className = ''
}) => {
  // Handle simple string array features (for plan cards)
  if (typeof features[0] === 'string') {
    const stringFeatures = features as string[];
    
    return (
      <div className={`space-y-3 text-left ${className}`}>
        {stringFeatures.map((feature, index) => (
          <div key={index} className="flex items-start gap-3 text-left">
            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700 leading-relaxed text-left">{feature}</span>
          </div>
        ))}
      </div>
    );
  }

  // Handle detailed PricingFeature objects (for comparison tables)
  const detailedFeatures = features as PricingFeature[];
  
  if (variant === 'comparison') {
    // Return feature data for table rendering - this will be used by parent component
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {detailedFeatures.map((feature, index) => {
        const featureValue = planId ? (
          planId === 'essential' ? feature.essential :
          planId === 'advanced' ? feature.advanced :
          planId === 'enterprise' ? feature.enterprise :
          false
        ) : true;

        // Skip features not included in this plan
        if (featureValue === false) return null;

        const CategoryIcon = feature.category ? getCategoryIcon(feature.category) : null;
        const categoryColor = feature.category ? getCategoryColor(feature.category) : '';

        return (
          <div key={index} className="flex items-start gap-3">
            <FeatureIcon included={featureValue} variant={variant} />
            <div className="flex-1">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900">{feature.name}</span>
                  {feature.description && (
                    <div className="text-xs text-gray-600 mt-1">{feature.description}</div>
                  )}
                  {typeof featureValue === 'string' && (
                    <div className="text-xs text-primary font-medium mt-1">{featureValue}</div>
                  )}
                </div>
                {showCategories && CategoryIcon && feature.category && (
                  <Badge variant="outline" className={`text-xs ${categoryColor}`}>
                    <CategoryIcon className="h-3 w-3 mr-1" />
                    {feature.category}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};